import React, { createContext, useContext, useMemo } from 'react';

import { Schema } from 'zod';
import { useMaskContext } from './use-mask';
import { useMaskConfigContext } from './use-mask-config';
import { Nullable } from '@form-engine/core-util/typings';

export type MaskValidationContextValue<
    T extends Schema = Schema<Record<string, unknown>>,
> = Nullable<ReturnType<T['safeParse']>>;

export const MaskValidationContext =
    createContext<MaskValidationContextValue | null>(null);

export function MaskValidationProvider({
    children,
}: {
    children?: React.ReactNode;
}) {
    const { state } = useMaskContext();
    const { schema } = useMaskConfigContext();
    const value = useMemo(
        () => schema?.safeParse(state) ?? null,
        [state, schema]
    );

    return (
        <MaskValidationContext.Provider value={value}>
            {children}
        </MaskValidationContext.Provider>
    );
}

export function useMaskValidationContext() {
    return useContext(MaskValidationContext) ?? null;
}

export function useMaskPropertyError(propertyName: string) {
    const context = useContext(MaskValidationContext);

    return useMemo(() => {
        if (!context || context.success === true) return null;

        for (const issue of context.error.issues ?? []) {
            if (issue.path[0] === propertyName) {
                return issue;
            }
        }

        return null;
    }, [context, propertyName]);
}
